import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import FormAlert from "./FormAlert";
import { useEventsByOwner } from "./../util/db";
import "./DashboardEvents.scss";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

function DashboardEvents({ uid }) {
  const {
    data: items,
    status: itemsStatus,
    error: itemsError,
  } = useEventsByOwner(uid);

  // Mobile responsiveness state
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 768
  );

  const isMobile = screenWidth < 768;
  const isSmallMobile = screenWidth < 576;

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setScreenWidth(window.innerWidth);
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  // Format relative date
  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }
    if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    }
    if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return months === 1 ? "1 month ago" : `${months} months ago`;
    }
    const years = Math.floor(diffInDays / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };

  // Get score color based on performance (updated for better UX)
  const getScoreColor = (score) => {
    if (!score && score !== 0) return "#6c757d"; // Gray for no score
    if (score <= 0) return "#28a745"; // Green for under par
    if (score <= 3) return "#007bff"; // Blue for good scores (1-3 over)
    if (score <= 7) return "#ffc107"; // Yellow for average scores (4-7 over)
    return "#fd7e14"; // Orange for high scores (8+ over, no red to avoid error appearance)
  };

  const itemsAreEmpty = !items || items.length === 0;

  return (
    <>
      {itemsError && (
        <div className="mb-3">
          <FormAlert type="error" message={itemsError.message} />
        </div>
      )}

      <Card
        className="mb-3"
        style={{
          boxShadow: isMobile
            ? "0 4px 16px rgba(45, 8, 63, 0.12), 0 2px 8px rgba(45, 8, 63, 0.08)"
            : "0 8px 24px rgba(45, 8, 63, 0.15), 0 4px 12px rgba(45, 8, 63, 0.1), 0 2px 6px rgba(45, 8, 63, 0.08)",
          border: "none",
          borderRadius: isSmallMobile ? "12px" : isMobile ? "16px" : "20px",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, #ffffff 0%, rgba(248, 249, 250, 0.9) 100%)",
          position: "relative",
          transition: "all 0.3s ease",
        }}
      >
        {/* Enhanced top accent border */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: isSmallMobile ? "3px" : "4px",
            background:
              "linear-gradient(90deg, rgba(45, 8, 63, 0.9) 0%, rgba(45, 8, 63, 0.7) 30%, rgba(45, 8, 63, 0.5) 50%, rgba(45, 8, 63, 0.7) 70%, rgba(45, 8, 63, 0.9) 100%)",
            borderRadius: `${
              isSmallMobile ? "12px" : isMobile ? "16px" : "20px"
            } ${isSmallMobile ? "12px" : isMobile ? "16px" : "20px"} 0 0`,
            boxShadow: "0 1px 3px rgba(45, 8, 63, 0.3)",
          }}
        />

        <Card.Header style={{ background: "#2D083F", color: "white" }}>
          <div className="d-flex align-items-center justify-content-between">
            <Card.Title className="mb-0" style={{ color: "white" }}>
              My Events
            </Card.Title>
            {items && items.length > 0 && (
              <span
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {items.length} {items.length === 1 ? "Event" : "Events"}
              </span>
            )}
          </div>
        </Card.Header>
        <Card.Body
          style={{
            background: "transparent",
            padding: isSmallMobile ? "16px" : isMobile ? "20px" : "24px",
          }}
        >
          {itemsStatus === "loading" && (
            <div className="py-4 text-center">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(45, 8, 63, 0.05) 0%, rgba(248, 249, 250, 0.8) 100%)",
                  borderRadius: "16px",
                  padding: "32px",
                  border: "1px solid rgba(45, 8, 63, 0.1)",
                }}
              >
                <Spinner
                  animation="border"
                  style={{
                    color: "#2D083F",
                    width: "48px",
                    height: "48px",
                    borderWidth: "3px",
                  }}
                >
                  <span className="sr-only">Loading...</span>
                </Spinner>
                <div
                  className="mt-3"
                  style={{
                    color: "#2D083F",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  Loading your events...
                </div>
              </div>
            </div>
          )}

          {itemsStatus !== "loading" && itemsAreEmpty && (
            <div className="py-5 text-center">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(45, 8, 63, 0.03) 0%, rgba(248, 249, 250, 0.9) 100%)",
                  borderRadius: "20px",
                  padding: "48px 32px",
                  border: "2px dashed rgba(45, 8, 63, 0.2)",
                  maxWidth: "400px",
                  margin: "0 auto",
                }}
              >
                <div className="mb-4">
                  <div
                    style={{
                      fontSize: "64px",
                      marginBottom: "16px",
                      filter: "drop-shadow(0 2px 8px rgba(45, 8, 63, 0.2))",
                    }}
                  >
                    🎯
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#2D083F",
                    marginBottom: "12px",
                  }}
                >
                  No Events Yet
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    color: "#6c757d",
                    lineHeight: "1.5",
                  }}
                >
                  Start your disc golf journey!
                  <br />
                  <small className="text-muted">
                    Events you participate in will appear here
                  </small>
                </div>
              </div>
            </div>
          )}

          {itemsStatus !== "loading" && items && items.length > 0 && (
            <div
              style={{
                gap: isSmallMobile ? "16px" : "20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {items.map((item, index) => (
                <div
                  key={index}
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)",
                    borderRadius: isSmallMobile ? "12px" : "16px",
                    padding: isSmallMobile ? "20px" : "24px",
                    border: "1px solid rgba(45, 8, 63, 0.1)",
                    boxShadow:
                      "0 4px 12px rgba(45, 8, 63, 0.08), 0 2px 4px rgba(45, 8, 63, 0.04)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(45, 8, 63, 0.15), 0 4px 8px rgba(45, 8, 63, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(45, 8, 63, 0.08), 0 2px 4px rgba(45, 8, 63, 0.04)";
                  }}
                >
                  {/* Subtle left accent */}
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      background: `linear-gradient(180deg, ${getScoreColor(
                        item.score
                      )} 0%, ${getScoreColor(item.score)}80 100%)`,
                      borderRadius: "0 2px 2px 0",
                    }}
                  />

                  <div
                    className={`d-flex ${
                      isMobile
                        ? "justify-content-between align-items-start"
                        : "justify-content-between align-items-start"
                    }`}
                    style={{
                      gap: isSmallMobile ? "8px" : "12px",
                    }}
                  >
                    <div
                      className="flex-grow-1"
                      style={{
                        marginLeft: "12px",
                        minWidth: 0, // Allow shrinking
                        maxWidth: isMobile ? "calc(100% - 90px)" : "none", // Reserve space for score on mobile
                      }}
                    >
                      {/* Event Name */}
                      <Link
                        to={`/event?event=${item.event_id}`}
                        style={{
                          textDecoration: "none",
                          display: "block",
                          marginBottom: "8px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "none";
                        }}
                      >
                        <h5
                          style={{
                            color: "#2D083F",
                            fontSize: isSmallMobile
                              ? "16px"
                              : isMobile
                              ? "18px"
                              : "22px",
                            fontWeight: "700",
                            marginBottom: "4px",
                            textShadow: "0 1px 2px rgba(45, 8, 63, 0.1)",
                            transition: "color 0.2s ease",
                            lineHeight: "1.2",
                            wordBreak: "break-word",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#1a0529";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#2D083F";
                          }}
                        >
                          🎯 {item.events.name}
                        </h5>
                      </Link>

                      {/* Event Date */}
                      {item.events.date && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "4px",
                            flexWrap: isSmallMobile ? "nowrap" : "wrap",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              marginRight: "4px",
                              opacity: 0.7,
                              flexShrink: 0,
                            }}
                          >
                            📅
                          </span>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: isSmallMobile ? "nowrap" : "wrap",
                              gap: isSmallMobile ? "4px" : "8px",
                              alignItems: "center",
                              overflow: "hidden",
                            }}
                          >
                            <span
                              style={{
                                color: "#2D083F",
                                fontSize: isSmallMobile ? "12px" : "14px",
                                fontWeight: "600",
                                whiteSpace: isSmallMobile ? "nowrap" : "normal",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {new Date(item.events.date).toLocaleDateString(
                                "en-US",
                                isSmallMobile
                                  ? { month: "short", day: "numeric" }
                                  : {
                                      weekday: "short",
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    }
                              )}
                            </span>
                            {!isSmallMobile && (
                              <span
                                style={{
                                  color: "#6c757d",
                                  fontSize: "12px",
                                  padding: "2px 8px",
                                  background: "rgba(108, 117, 125, 0.1)",
                                  borderRadius: "12px",
                                  fontWeight: "500",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatRelativeDate(item.events.date)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Score Display - Optimized for Small Mobile */}
                    {item.score !== null && item.score !== undefined && (
                      <div
                        style={{
                          background: `linear-gradient(135deg, ${getScoreColor(
                            item.score
                          )}15 0%, ${getScoreColor(item.score)}08 100%)`,
                          border: `2px solid ${getScoreColor(item.score)}30`,
                          borderRadius: isSmallMobile ? "8px" : "12px",
                          padding: isSmallMobile ? "6px 10px" : "12px 16px",
                          minWidth: isSmallMobile ? "55px" : "75px",
                          maxWidth: isSmallMobile ? "65px" : "85px",
                          flexShrink: 0,
                          alignSelf: "flex-start",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: isSmallMobile ? "9px" : "12px",
                            fontWeight: "600",
                            color: "#6c757d",
                            textTransform: "uppercase",
                            letterSpacing: "0.3px",
                            marginBottom: "2px",
                            textAlign: "center",
                            lineHeight: "1",
                          }}
                        >
                          {isSmallMobile ? "Score" : "Final Score"}
                        </div>
                        <div
                          style={{
                            fontSize: isSmallMobile
                              ? "16px"
                              : isMobile
                              ? "22px"
                              : "26px",
                            fontWeight: "800",
                            color: getScoreColor(item.score),
                            textAlign: "center",
                            textShadow: `0 2px 4px ${getScoreColor(
                              item.score
                            )}30`,
                            lineHeight: "1",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          {item.score}
                        </div>
                        {item.score <= 0 && !isSmallMobile && (
                          <div
                            style={{
                              fontSize: "9px",
                              color: "#28a745",
                              textAlign: "center",
                              marginTop: "2px",
                              fontWeight: "600",
                              lineHeight: "1",
                            }}
                          >
                            Under Par! 🎉
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}

export default DashboardEvents;
